import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  contestantLabel,
  formatMs,
  formatPct,
  formatUsd,
  scoreTask,
  TASK_META,
  type LabelValue,
  type ModelLabCaseSummary,
  type ModelLabResultSummary,
  type Task,
} from '@/lib/model-lab'

interface Props {
  readonly task: Task
  readonly contestantKeys: readonly string[]
  readonly cases: readonly ModelLabCaseSummary[]
  readonly results: readonly ModelLabResultSummary[]
  readonly labels: ReadonlyMap<string, LabelValue>
}

export function Scoreboard({ task, contestantKeys, cases, results, labels }: Props) {
  const rows = scoreTask(task, contestantKeys, cases, results, labels)
  const bestAccuracy = Math.max(...rows.map((r) => (r.labeled ? r.correct / r.labeled : -1)))
  const isSms = task === 'sms'

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{TASK_META[task].label}</CardTitle>
        <CardDescription>
          {isSms
            ? 'Score = share of this model\'s texts you rated good.'
            : 'Accuracy is measured only on conversations you have labelled. "Same as production" is for reference — production can be wrong too.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">{isSms ? 'Rated good' : 'Accuracy'}</TableHead>
              <TableHead className="text-right">Labelled</TableHead>
              {!isSms && <TableHead className="text-right">Same as production</TableHead>}
              <TableHead className="text-right">Valid output</TableHead>
              <TableHead className="text-right">p50 / p95</TableHead>
              <TableHead className="text-right">Avg tokens in / out</TableHead>
              <TableHead className="text-right" title="Estimated spend on this decision type in this run, at list price">
                Spent
              </TableHead>
              <TableHead className="text-right" title="Projected cost of 1,000 decisions at this model's average tokens and list price — not money spent">
                Est. per 1,000
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const accuracy = r.labeled ? r.correct / r.labeled : null
              const isBest = accuracy !== null && accuracy === bestAccuracy && r.labeled > 0
              return (
                <TableRow key={r.contestantKey}>
                  <TableCell className="font-mono text-xs">{contestantLabel(r.contestantKey)}</TableCell>
                  <TableCell className={`text-right font-semibold ${isBest ? 'text-emerald-700' : ''}`}>
                    {formatPct(r.correct, r.labeled)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{r.labeled}</TableCell>
                  {!isSms && (
                    <TableCell className="text-right">{formatPct(r.matchesProduction, r.productionComparable)}</TableCell>
                  )}
                  <TableCell className="text-right" title={`${r.parseErrors} unparseable, ${r.apiErrors} API errors/timeouts`}>
                    {formatPct(r.ok, r.total)}
                    {(r.parseErrors > 0 || r.apiErrors > 0) && (
                      <span className="ml-1 text-xs text-destructive">({r.parseErrors + r.apiErrors} failed)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatMs(r.p50LatencyMs)} / {formatMs(r.p95LatencyMs)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {r.avgInputTokens === null ? '—' : `${Math.round(r.avgInputTokens)} / ${Math.round(r.avgOutputTokens ?? 0)}`}
                  </TableCell>
                  <TableCell className="text-right font-medium">{formatUsd(r.spentUsd)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{formatUsd(r.costPer1k)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
